import { useMutation } from '@tanstack/react-query'
import { AppDialogRefType } from 'components'
import dayjs from 'dayjs'
import { ApiQueryKeys } from 'globals/constants'
import { useSnackbar } from 'notistack'
import { useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { NewsAlertApi, NewsAlertPayload, NewsAlertViewModel, PaginationFilterModel, ShowAlert } from 'sdk'

export const useNewsAlert = () => {
  const newsAlertApi = new NewsAlertApi()
  const { enqueueSnackbar } = useSnackbar()
  const [filter, setFilter] = useState<PaginationFilterModel>(new PaginationFilterModel())
  const { control, setValue, handleSubmit, getValues, reset } = useForm<NewsAlertPayload>({
    mode: 'onChange',
  })
  const dialogRef = useRef<AppDialogRefType>(null)
  const [idToUpdate, setIdToUpdate] = useState<string | null>(null)

  const {
    mutateAsync: GetFilteredAlerts,
    isPending: isGetting,
    data: filterdAlerts,
  } = useMutation({
    mutationFn: () => newsAlertApi.getNewsAlerts(filter),
    mutationKey: [ApiQueryKeys.newsAlert.getNewsAlerts],
    onError: (error) => enqueueSnackbar(error.message, { variant: 'error' }),
  })
  const { mutateAsync: AddNewsAlert, isPending: isAdding } = useMutation({
    mutationFn: (obj: NewsAlertPayload) => newsAlertApi.addNewsAlerts(obj),
    mutationKey: [ApiQueryKeys.newsAlert.addNewsAlert],
    onError: (error) => enqueueSnackbar(error.message, { variant: 'error' }),
    onSuccess: () => {
      dialogRef.current?.closeDialog!()
      enqueueSnackbar('News Alert added successfuly', { variant: 'success' })
      GetFilteredAlerts()
    },
  })
  const { mutate: EditNewsAlert, isPending: isEditing } = useMutation({
    mutationFn: (obj: NewsAlertPayload) => newsAlertApi.updateNewsAlert(obj, idToUpdate!),
    mutationKey: [ApiQueryKeys.newsAlert.updateNewsAlert],
    onError: (error) => enqueueSnackbar(error.message, { variant: 'error' }),
    onSuccess: () => {
      dialogRef.current?.closeDialog!()
      enqueueSnackbar('News Alert edited successfuly', { variant: 'success' })
      GetFilteredAlerts()
    },
  })
  const { mutate: DeleteNewsAlert, isPending: isDeleting } = useMutation({
    mutationFn: (id: string) => newsAlertApi.deleteNewsAlert(id),
    mutationKey: [ApiQueryKeys.newsAlert.deleteNewsAlert],
    onError: (error) => enqueueSnackbar(error.message, { variant: 'error' }),
    onSuccess: () => {
      enqueueSnackbar('News Alert deleted successfuly', { variant: 'success' })
      GetFilteredAlerts()
    },
  })

  const handleDeleteNewsAlert = (id: string) => {
    ShowAlert('question', 'Are you sure you want to delete this news alert?').then((x) => {
      if (x.isConfirmed) {
        DeleteNewsAlert(id)
      }
    })
  }
  useEffect(() => {
    GetFilteredAlerts()
  }, [filter])

  const openDialog = (alert?: NewsAlertViewModel) => {
    if (!alert) {
      setIdToUpdate(null)
      reset({})
    } else {
      setIdToUpdate(alert.Id)
      reset({
        StartDate: dayjs(alert.StartDate ?? new Date()) as any,
        ArticleDate: dayjs(alert.ArticleDate ?? new Date()) as any,
        Title: alert.Title,
        ArticleUrl: alert.ArticleUrl,
        ViewAllUrl: alert.ViewAllUrl,
        Description: alert.Description,
      })
    }
    dialogRef.current?.openDialog!()
  }
  const submitForm = (data: NewsAlertPayload) => {
    if (!idToUpdate) {
      AddNewsAlert(data)
    } else {
      EditNewsAlert(data)
    }
  }
  return {
    filter,
    idToUpdate,
    setFilter,
    GetFilteredAlerts,
    filterdAlerts,
    isGetting,
    AddNewsAlert,
    isAdding,
    EditNewsAlert,
    isEditing,
    isDeleting,
    control,
    setValue,
    reset,
    handleSubmit,
    submitForm,
    dialogRef,
    openDialog,
    handleDeleteNewsAlert,
  }
}
